/**
 * Zod validation schemas for Story entities
 *
 * These schemas validate API requests and provide runtime type safety.
 * Used by both frontend (form validation) and backend (input validation).
 */

import { z } from 'zod';
import { AgeBandSchema, GenerationModeSchema } from './common-schemas';

/**
 * Character reference in a story
 */
export const CharacterRefSchema = z.object({
  characterId: z.string().min(1),
  role: z.enum(['protagonist', 'supporting']),
});

/**
 * World reference in a story
 */
export const WorldRefSchema = z.object({
  worldId: z.string().min(1),
});

/**
 * Zod schema for ChoicePath
 * Used in interactive decision points (FR45-47)
 */
export const ChoicePathSchema = z.object({
  label: z.string().min(1),
  paragraphs: z.array(z.string()),
});

/**
 * Zod schema for StorySection discriminated union
 * Replaces flat content string with inline markers
 *
 * @see docs/firestore-schema.md for schema details
 */
export const StorySectionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('episode_start'), number: z.number().int().positive() }),
  z.object({ type: z.literal('paragraph'), content: z.string().min(1) }),
  z.object({
    type: z.literal('choice'),
    prompt: z.string().min(1),
    pathA: ChoicePathSchema,
    pathB: ChoicePathSchema,
  }),
  z.object({ type: z.literal('epilogue_start') }),
]);

/**
 * Zod schema for sections array
 */
export const StorySectionsSchema = z.array(StorySectionSchema);

/**
 * Story translation validation
 */
export const StoryTranslationSchema = z.object({
  title: z.string().min(1).max(200),
  /** @deprecated Use sections instead - kept for backward compatibility */
  content: z.string().min(100).optional(),
  /** Structured story content (replaces content string) */
  sections: StorySectionsSchema.optional(),
  // TD-VALID-1: Removed .max(1000) - GPT doesn't respect char limits in structured output
  synopsis: z.string().min(10),
  teachBackQuestions: z.array(z.string()).min(1).max(5),
  // Same safety net as teachBackQuestions/parentTips: no array length constraint.
  // Prompt asks for 3, but accept whatever GPT returns (0-N). Frontend shows up to 3.
  sequelProposals: z.array(z.string().min(20).max(500)).optional(),
  coverImageUrl: z.string().url(),
});

/**
 * Story metadata validation
 */
export const StoryMetadataSchema = z.object({
  generationTimeMs: z.number().min(0).max(180000), // Max 3 minutes
  synopsisApproved: z.boolean(),
  wishesAdded: z.string().max(500).nullable(),
  interactiveChoices: z
    .array(
      z.object({
        episodeIndex: z.number().min(0),
        choiceMade: z.enum(['A', 'B']),
      })
    )
    .optional(),
  targetAudience: z.enum(['boy', 'girl', 'any']),
  generationMode: GenerationModeSchema.optional(),
});

/**
 * Complete Story validation
 */
export const StorySchema = z.object({
  id: z.string().min(1),
  householdId: z.string().min(1),
  ageBand: AgeBandSchema,
  translations: z.object({
    en: StoryTranslationSchema,
    fi: StoryTranslationSchema,
  }),
  characters: z.array(CharacterRefSchema).min(1).max(5),
  worlds: z.array(WorldRefSchema).min(1).max(3),
  theme: z.string().min(1).max(100),
  momentsType: z.string().nullable(),
  metadata: StoryMetadataSchema,
  rating: z.enum(['up', 'down']).nullable(),
  favorited: z.boolean(),
  createdAt: z.date(),
  episodeCount: z.number().min(1).max(5),
  parentStoryId: z.string().min(1).optional(),
  mainCharacterIds: z.array(z.string().min(1)).optional(),
});

/**
 * Base fields shared between frontend and backend request schemas.
 * Functions package extends these with server-only fields via .extend().
 *
 * IMPORTANT: .nullable().optional() is used because the frontend sends
 * null for unset optional fields. Both packages must accept null.
 *
 * NOTE: The shared/ schemas previously had stricter validators (.min(1),
 * required fields). This is intentionally loosened to match the actual
 * API contract. The UI enforces required-ness (e.g., targetAudience
 * defaults to "any"), not the schema.
 *
 * S16.10: momentsCustomization dropped — unused in functions/ and frontend.
 */
export const BaseCreateStoryFieldsSchema = z.object({
  ageBand: AgeBandSchema,
  theme: z.string().nullable().optional(),
  targetAudience: z.enum(['boy', 'girl', 'any']).nullable().optional(),
  characterIds: z.array(z.string()).max(2).nullable().optional(),
  worldIds: z.array(z.string()).max(1).nullable().optional(),
  wishes: z.string().max(500).nullable().optional(),
  momentsType: z.string().nullable().optional(),
  parentStoryId: z.string().min(1).optional(),
  mainCharacterIds: z.array(z.string().min(1)).optional(),
});

/** Synopsis extends Create base with sequelProposal — no field duplication */
export const BaseGenerateSynopsisFieldsSchema = BaseCreateStoryFieldsSchema.extend({
  sequelProposal: z.string().min(20).max(500).optional(),
});

// Frontend-facing schemas (same as base — no server-only fields needed)
// Backward compatible: all existing imports continue to work.
export const CreateStoryRequestSchema = BaseCreateStoryFieldsSchema;
export const GenerateSynopsisRequestSchema = BaseGenerateSynopsisFieldsSchema;

/**
 * GetStoriesRequest validation
 * Used for GET /stories endpoint
 */
export const GetStoriesRequestSchema = z.object({
  ageBand: AgeBandSchema.optional(),
  limit: z.number().min(1).max(100).optional().default(20),
  cursor: z.string().optional(),
});
